import React, { useEffect, useState } from "react";
import axios from "axios";
import { Reveal, SectionLabel } from "../components/primitives";
import { ArrowRight, MapPin, Briefcase, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Careers() {
  const [openings, setOpenings] = useState([]);
  const [activeRole, setActiveRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", linkedin: "", portfolio_url: "", cover_note: "" });

  useEffect(() => {
    axios.get(`${API}/fund/openings`).then((r) => setOpenings(r.data.openings || [])).catch(() => setOpenings([]));
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!activeRole) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, { ...form, role: activeRole.title });
      setSubmitted(true);
      setForm({ full_name: "", email: "", linkedin: "", portfolio_url: "", cover_note: "" });
      toast.success("Application received. We'll be in touch.");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <section className="pt-20 pb-12 lg:pt-32 lg:pb-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionLabel>Careers</SectionLabel>
          <h1 className="font-display font-bold tracking-[-0.035em] leading-[0.94] text-5xl sm:text-7xl lg:text-[104px] text-[#111A15] max-w-5xl">
            Build with us.
          </h1>
          <p className="mt-10 max-w-2xl text-[17px] text-[#4A5E51] leading-relaxed">
            We're building India's first ESG led fund. If you want to deploy capital where it matters most — and roll up your sleeves alongside founders — we want to talk.
          </p>
        </div>
      </section>

      <section className="border-y border-[#E5E5E0] bg-[#E8ECE9]/40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 py-5 border-b border-[#E5E5E0] font-mono text-[10px] uppercase tracking-[0.22em] text-[#4A5E51]">
            <div className="col-span-6 lg:col-span-7">Role</div>
            <div className="col-span-3 lg:col-span-2">Team</div>
            <div className="hidden lg:block lg:col-span-2">Location</div>
            <div className="col-span-3 lg:col-span-1 text-right">—</div>
          </div>
          {openings.map((o, i) => (
            <Reveal key={o.id} delay={i * 60}>
              <button
                type="button"
                data-testid={`opening-${o.id}`}
                onClick={() => { setActiveRole(o); setSubmitted(false); document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
                className="w-full grid grid-cols-12 items-center py-7 lg:py-9 border-b border-[#E5E5E0] last:border-b-0 group hover:bg-white transition-colors text-left"
              >
                <div className="col-span-6 lg:col-span-7 px-1">
                  <p className="font-display font-medium tracking-tight text-2xl sm:text-3xl lg:text-[34px] text-[#111A15] group-hover:text-[#C44900] transition-colors leading-tight">{o.title}</p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#4A5E51] mt-2">— {o.type}</p>
                </div>
                <div className="col-span-3 lg:col-span-2 text-[14px] text-[#4A5E51] flex items-center gap-2"><Briefcase className="w-3.5 h-3.5" />{o.team}</div>
                <div className="hidden lg:flex lg:col-span-2 text-[14px] text-[#4A5E51] items-center gap-2"><MapPin className="w-3.5 h-3.5" />{o.location}</div>
                <div className="col-span-3 lg:col-span-1 flex justify-end pr-2">
                  <ArrowRight className="w-5 h-5 text-[#4A5E51] group-hover:text-[#C44900] group-hover:translate-x-1 transition-all" />
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="apply-form" className="py-24 lg:py-32">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <SectionLabel>Apply</SectionLabel>
          <h2 className="font-display font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#111A15] leading-[1.05] mb-10">
            {activeRole ? <>Applying for <span className="text-[#C44900]">{activeRole.title}</span>.</> : "Pick a role above and tell us about you."}
          </h2>

          {submitted ? (
            <div data-testid="application-success" className="border border-[#1A2F24] bg-white p-10 flex items-start gap-5">
              <CheckCircle2 className="w-7 h-7 text-[#1A2F24] mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <p className="font-display font-semibold text-2xl text-[#111A15] mb-2">Application received.</p>
                <p className="text-[14px] text-[#4A5E51] leading-relaxed">Thanks for applying to Folks Funds. We review every application and will be in touch within two weeks.</p>
                <button onClick={() => setSubmitted(false)} className="mt-6 text-[13px] font-mono uppercase tracking-[0.18em] text-[#C44900] hover:underline">Submit another →</button>
              </div>
            </div>
          ) : (
            <form data-testid="application-form" onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
              <Field label="Full name" required>
                <input data-testid="apply-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Email" required>
                <input data-testid="apply-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              </Field>
              <Field label="LinkedIn URL">
                <input data-testid="apply-linkedin" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} className={inputCls} placeholder="https://linkedin.com/in/…" />
              </Field>
              <Field label="Portfolio / Resume URL">
                <input data-testid="apply-portfolio" value={form.portfolio_url} onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })} className={inputCls} placeholder="https://…" />
              </Field>
              <Field label="Why Folks Funds?" className="md:col-span-2">
                <textarea data-testid="apply-cover" rows={5} value={form.cover_note} onChange={(e) => setForm({ ...form, cover_note: e.target.value })} className={`${inputCls} resize-none`} placeholder="A short note on what excites you about climate, India, and the Folks ecosystem." />
              </Field>
              <div className="md:col-span-2 flex flex-wrap items-center gap-4 mt-4">
                <button data-testid="apply-submit" type="submit" disabled={submitting || !activeRole} className="inline-flex items-center gap-2 bg-[#1A2F24] text-white px-8 py-4 text-[14px] font-medium hover:bg-[#111A15] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  {submitting ? "Submitting" : "Send application"}
                </button>
                {!activeRole && <span className="text-[13px] text-[#4A5E51]">Select a role above to enable submission.</span>}
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-transparent border-b-2 border-[#E5E5E0] py-3 text-[15px] text-[#111A15] focus:outline-none focus:border-[#1A2F24] transition-colors placeholder:text-[#9DB0A3]";

function Field({ label, children, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#4A5E51] mb-2">— {label}{required && <span className="text-[#C44900]"> *</span>}</span>
      {children}
    </label>
  );
}
